/**
 * Voice providers service — STT / TTS configuration for the AI voice agent.
 *
 * Backs Settings → Voice providers. Mirrors ai.spectraiq.backend.voice
 * .controller.VoiceProviderSettingsController (/v1/voice-providers).
 *
 * Response unwrapping: same `r.data?.data ?? r.data` idiom as the other
 * services (backend wraps everything in ApiResponse<T>).
 *
 * Secrets: the backend never returns a stored API key — only a masked form
 * plus `hasCredentials`. Sending `apiKey: null` (or omitting it) on save
 * keeps the stored secret; an empty string clears it.
 */
import { api } from '../api';

const unwrap = (r) => r.data?.data ?? r.data;

export default {
  /**
   * Current settings, masked. A 404 means "not configured yet" — callers
   * treat it as a clean slate, not an error.
   */
  get: () =>
    api.get('/v1/voice-providers').then(unwrap).catch((e) => {
      if (e?.response?.status === 404) return null;
      throw e;
    }),

  /** Save. Payload: { stt: {...}, tts: {...} } — see backend SaveVoiceProvidersRequest. */
  save: (payload) => api.put('/v1/voice-providers', payload).then(unwrap),

  /** Verify stored credentials against both providers' APIs. */
  test: () => api.post('/v1/voice-providers/test').then(unwrap),

  /**
   * Short-lived token for the browser mic test (SPEC-VOX-303).
   * Returns { token, wsUrl, expiresAt }; wsUrl may be blank when no public
   * voice-loop URL is configured (the MicTestCard falls back to localhost).
   */
  micTestToken: () => api.post('/v1/voice-providers/mic-test-token').then(unwrap),

  /**
   * Advanced engine (Retell) — single-use Web Call access token for the
   * browser mic test. Returns { accessToken, callId, agentId }.
   *
   * `phone` is the number this call runs as, handed to the agent's booking
   * tools as customer_phone. Required by the Advanced-engine mic test — there
   * is no server-side default number.
   */
  webCallToken: (phone) =>
    api.post('/v1/voice-providers/web-call-token', phone ? { phone } : {}).then(unwrap),

  /**
   * Rotate the Advanced-engine MCP tool key. Returns { toolKey } — plaintext
   * shown ONCE; paste into the Retell agent's MCP headers as X-Retell-Tool-Key.
   */
  regenerateToolKey: () => api.post('/v1/voice-providers/retell-tool-key').then(unwrap),
};
